import { Box, CssBaseline } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import isEmpty from "lodash/isEmpty";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import LoginForm from "../../components/pages-components/login-page/login-form/LoginForm";
import { useToast } from "../../context/ToasterContext";
import { useAppDispatch } from "../../hooks/storeHooks";
import { ROUTE_PATHS } from "../../routes";
import { resetApiError, setDeviceTokenGlobally } from "../../store/user/userSlice";
import { loginUserThunk } from "../../store/user/userThunk";
import { REDUX_REQUEST_STATUS, iApiError } from "../../types/commonTypes";
import STATUS_CODES from "../../constants/statusCodesConstants";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../types/coreButtonTypes";
import { iLoginPayload } from "../../types/userType";
import { FIREBASE_TOKEN_SYNC_STATUS } from "../../constants/commonConstants";
import { getLocalStorageItem, removeItemFromLocalStorage } from "../../helpers/localStorageHelpers";
import "./login.css";

const loginFromFieldsMap = [
	{
		id: "email",
		label: "Email",
		type: "email",
		placeholder: "Enter your email",
		className: "form-group",
		error: false,
	},
	{
		id: "password",
		label: "Password",
		type: "password",
		placeholder: "Enter password",
		className: "form-group",
		required: false,
	},
];

export default function LoginPage() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { showToast } = useToast();

	useEffect(() => {
		const deviceToken = getLocalStorageItem(FIREBASE_TOKEN_SYNC_STATUS);
		dispatch(setDeviceTokenGlobally(null));
		if (deviceToken) {
			removeItemFromLocalStorage(FIREBASE_TOKEN_SYNC_STATUS);
		}
	}, []);

	const onSubmitForm = async (values: iLoginPayload, errorCallBack: () => void) => {
		var validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
		if (isEmpty(values.email)) {
			showToast("Please enter the email", () => onToasterClose(), "error");
		} else if (isEmpty(values.password)) {
			showToast("Please enter the password", () => onToasterClose(), "error");
		} else if (values.password.length < 5) {
			showToast("Password length should be greater then or equal 6", () => onToasterClose(), "error");
		} else if (!validRegex.test(values.email)) {
			showToast("Please enter valid email", () => onToasterClose(), "error");
		} else {
			const resp = await dispatch(loginUserThunk({ ...values }));
			if (resp.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
				// No terms check here. The gate is mounted once in DashboardLayout,
				// which this route lands inside, and it keys on the API's version-
				// aware `status` rather than the old `tcAccepted` boolean.
				const { message, data } = unwrapResult(resp);
				!data.user.mustChangePassword && showToast(message);
				navigate(
					data.user.mustChangePassword ? ROUTE_PATHS.CHANGE_PASSWORD : ROUTE_PATHS.AI_DIRECT_REQUEST,
					{ replace: true },
				);
				return;
			}

			const error = resp.payload as iApiError | undefined;
			if (error?.statusCode === STATUS_CODES.AUTH.TEMPORARY_PASSWORD_EXPIRED) {
				showToast(error.message, () => onToasterClose(), "error");
				return;
			}
			errorCallBack();
		}
	};

	const onToasterClose = () => {
		dispatch(resetApiError());
	};

	return (
		<>
			<CssBaseline />
			<Helmet>
				<title>Login</title>
			</Helmet>
			<Box
				className="login-page"
				sx={{
					backgroundImage: "url('/assets/login/login-page-bg-img-large.png')",
				}}
			>
				<Box className="login-holder">
					<picture className="logo">
						<img src="/assets/login/login-logo.png" alt="logo" />
					</picture>
					<LoginForm
						loginFormTitle="Welcome!"
						loginFormPara="Enter your login details to continue"
						loginFromFields={loginFromFieldsMap}
						checkBoxLabel="Remember me"
						buttonText="Continue"
						fullWidth={true}
						disabled={false}
						coreBtnPill={true}
						onSubmit={onSubmitForm}
						variant={BUTTON_VARIANT.CONTAINED}
						color={BUTTON_COLOR.PRIMARY}
						formFieldStartText="Don’t have an account?"
						formFieldLinkText="Partner with us"
						formFieldEndText="to get started."
						forgotPasswordLink={{ label: "Forgot password?", href: ROUTE_PATHS.FORGOT_PASSWORD }}
					/>
				</Box>
			</Box>
		</>
	);
}
